// src/hooks/useIdleTimer.js
import { useEffect, useRef } from "react";

const ACTIVITY_KEY = "lastActivityTimestamp";
const THROTTLE_MS = 60000; // Write to localStorage at most once per minute

/**
 * Check if the session is stale (last activity was more than idleTime ago).
 * Call this on page load BEFORE rendering protected content.
 * @param {number} idleTime - Max idle duration in ms (default: 1 hour)
 * @returns {boolean} true if session is stale or no activity recorded
 */
export const isSessionStale = (idleTime = 3600000) => {
  const lastActivity = localStorage.getItem(ACTIVITY_KEY);
  if (!lastActivity) return false; // First visit — no timestamp yet, not stale
  return Date.now() - parseInt(lastActivity, 10) > idleTime;
};

/**
 * Clear the activity timestamp (call on logout).
 */
export const clearActivityTimestamp = () => {
  localStorage.removeItem(ACTIVITY_KEY);
};

export const resetActivityTimestamp = () => {
  localStorage.setItem(ACTIVITY_KEY, String(Date.now()));
};

/**
 * @param {() => void} onIdle - Callback when user is idle.
 * @param {number} idleTime - Idle timeout in ms (default: 1 hour).
 */
export const useIdleTimer = (onIdle, idleTime = 3600000) => {
  const timeoutId = useRef(null);
  const lastWriteRef = useRef(0);

  const persistActivity = () => {
    const now = Date.now();
    if (now - lastWriteRef.current >= THROTTLE_MS) {
      lastWriteRef.current = now;
      resetActivityTimestamp();
    }
  };

  const checkIdle = () => {
    const lastActivity = Number(localStorage.getItem(ACTIVITY_KEY));
    const remaining = idleTime - (Date.now() - lastActivity);
    if (remaining > 0) {
      timeoutId.current = setTimeout(checkIdle, remaining);
    } else {
      onIdle();
    }
  };

  const startTimer = () => {
    timeoutId.current = setTimeout(checkIdle, idleTime);
  };

  const clearTimer = () => {
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
    }
  };

  const resetTimer = () => {
    clearTimer();
    startTimer();
    persistActivity();
  };

  useEffect(() => {
    const events = [
      "mousemove",
      "keydown",
      "mousedown",
      "scroll",
      "touchstart",
    ];

    // Record initial activity and start the idle timer
    persistActivity();
    startTimer();

    events.forEach((event) => {
      window.addEventListener(event, resetTimer);
    });

    return () => {
      clearTimer();
      events.forEach((event) => {
        window.removeEventListener(event, resetTimer);
      });
    };
    // Mount-only setup; resetTimer/startTimer are recreated each render, so
    // including them would tear down and re-bind all listeners every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onIdle, idleTime]);

  return null;
};
